<script setup>
import {ref, computed, watch, onUnmounted, nextTick} from 'vue'
import {useFormStore} from '@/stores/form'
import FormFieldsets from './FormFieldsets'

const props = defineProps({
    formId: {
        type: String,
        required: true
    },
    showLegends: Boolean,
    hideHelpText: Boolean,
    showOptions: Boolean
})

const formStore = useFormStore(props.formId)

// --- Template Refs ---

const formRef = ref(null)

// --- Computed ---

const formShow = computed(() => formStore.show)

const currentData = computed(() => formStore)

const legends = computed(() => formStore.legends)

const clickedAnchor = computed(() => formStore.clickedAnchor)

const top = computed(() => {
    const firstLegend = legends.value?.[0]
    const fieldset = getFieldset(firstLegend)
    return fieldset ? fieldset.getBoundingClientRect().top : 0
})

const scrollableEl = computed(() => {
    return formRef.value?.closest('.sidebar-toggle-section')
})

// --- Methods ---

const getFieldset = (legend) => {
    if (legend && formRef.value) {
        const idSafeLegend = legend.replace(/[^A-Z0-9]+/gi, '_')
        return formRef.value.querySelector(`fieldset#${idSafeLegend}`)
    }
    return null
}

const scrollTop = () => {
    if (scrollableEl.value) {
        scrollableEl.value.scroll(0, 0)
    }
}

const scrollToLegend = (legend) => {
    const fieldset = getFieldset(legend)
    if (!fieldset) return

    // The form must be scrolled top before we obtain the fieldset's top position below.
    scrollTop()

    nextTick(() => {
        const targetTop = fieldset.getBoundingClientRect().top - top.value
        scrollableEl.value.scroll(0, targetTop)
    })
}

const save = () => {
    formStore.save()
}

// --- Watchers ---

watch(
    () => props.formId,
    () => {
        nextTick(() => {
            if (formRef.value) {
                formStore.setFormEl(formRef.value)
            }
            // Always scroll to the top when showing new forms.
            scrollTop()
        })
    },
    {immediate: true}
)

watch(clickedAnchor, (newValue) => {
    if (newValue) {
        scrollToLegend(newValue)
    }
})

// --- Lifecycle ---

onUnmounted(() => {
    formStore.$dispose()
})
</script>

<template>
    <form v-if="currentData" ref="formRef" autocomplete="off" novalidate @submit.prevent="save">
        <FormFieldsets
            v-if="formShow"
            :form-id="formId"
            :show-legends="showLegends"
            :hide-help-text="hideHelpText"
        />

        <Dump>{{
                {
                    isModified: formStore.isModified,
                    modifiedData: formStore.modifiedData,
                    currentData: formStore.currentData,
                    originalData: formStore.originalData
                }
            }}
        </Dump>
    </form>
</template>